import {
  Box,
  Container,
  VStack,
  Heading,
  Text,
  HStack,
  Button,
  Link,
  Collapse,
  IconButton,
  Image,
  Flex,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { Link as ScrollLink } from "react-scroll";
import { AiOutlineMenu, AiOutlineClose } from "react-icons/ai";
import Satellite from "../components/Satellites";
import Begin from "../components/Begin";
import FlightHeritage from "../components/FlightHeritage";
import CompanyExperience from "../components/CompanyExperience";
import Involvement from "../components/Involvement";
import SectionHeader from "../components/SectionHeader";

const MotionBox = motion(Box);

const NAV_ITEMS = [
  { id: "home", label: "Home" },
  { id: "about", label: "About" },
  { id: "flight", label: "Flight Heritage" },
  { id: "experience", label: "Experience" },
  { id: "involvement", label: "Involvement" },
  { id: "simulation", label: "Simulation" },
];

const linkStyles = {
  color: "white",
  borderBottom: "1px solid",
  borderColor: "whiteAlpha.400",
  _hover: { borderColor: "white", textDecoration: "none" },
};

function Home4() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <Box minH="100vh" bg="black" w="100%" overflowX="hidden">
      {/* Navigation */}
      <Box
        position="fixed"
        top={0}
        left={0}
        right={0}
        zIndex={1000}
        bg={scrolled ? "rgba(0, 0, 0, 0.72)" : "transparent"}
        backdropFilter={scrolled ? "blur(16px) saturate(150%)" : "none"}
        transition="all 0.3s ease"
        borderBottom="1px solid"
        borderColor={scrolled ? "whiteAlpha.200" : "transparent"}
      >
        <Container maxW="5xl" px={{ base: 6, md: 8 }} py={4}>
          <HStack justify="space-between" align="center">
            <ScrollLink to="home" smooth={true} duration={500}>
              <Heading
                fontSize="sm"
                fontFamily="mono"
                letterSpacing="0.14em"
                textTransform="uppercase"
                color="white"
                fontWeight="500"
                cursor="pointer"
              >
                Divij Garg
              </Heading>
            </ScrollLink>

            <HStack spacing={1} display={{ base: "none", md: "flex" }}>
              {NAV_ITEMS.map((item) => (
                <ScrollLink
                  key={item.id}
                  to={item.id}
                  smooth={true}
                  duration={500}
                  offset={-80}
                  spy={true}
                  activeClass="nav-link-active"
                >
                  <Button
                    variant="ghost"
                    size="sm"
                    fontFamily="mono"
                    fontSize="xs"
                    fontWeight="400"
                    letterSpacing="0.08em"
                    textTransform="uppercase"
                    color="whiteAlpha.600"
                    _hover={{ color: "white", bg: "whiteAlpha.100" }}
                  >
                    {item.label}
                  </Button>
                </ScrollLink>
              ))}
            </HStack>

            <IconButton
              aria-label="Toggle navigation menu"
              icon={menuOpen ? <AiOutlineClose /> : <AiOutlineMenu />}
              variant="ghost"
              color="white"
              fontSize="xl"
              display={{ base: "flex", md: "none" }}
              onClick={() => setMenuOpen((prev) => !prev)}
              _hover={{ bg: "whiteAlpha.200" }}
            />
          </HStack>
        </Container>

        {/* Mobile menu */}
        <Collapse in={menuOpen} animateOpacity>
          <Box
            display={{ base: "block", md: "none" }}
            bg="rgba(0, 0, 0, 0.92)"
            backdropFilter="blur(16px)"
            borderTop="1px solid"
            borderColor="whiteAlpha.200"
            px={6}
            py={4}
          >
            <VStack align="stretch" spacing={1}>
              {NAV_ITEMS.map((item) => (
                <ScrollLink
                  key={item.id}
                  to={item.id}
                  smooth={true}
                  duration={500}
                  offset={-80}
                  onClick={() => setMenuOpen(false)}
                >
                  <Button
                    variant="ghost"
                    justifyContent="flex-start"
                    w="100%"
                    fontFamily="mono"
                    fontSize="xs"
                    fontWeight="400"
                    letterSpacing="0.08em"
                    textTransform="uppercase"
                    color="whiteAlpha.700"
                    _hover={{ bg: "whiteAlpha.100", color: "white" }}
                  >
                    {item.label}
                  </Button>
                </ScrollLink>
              ))}
            </VStack>
          </Box>
        </Collapse>
      </Box>

      <Begin />

      {/* About */}
      <Box id="about" py={{ base: 20, md: 28 }}>
        <Container maxW="5xl" px={{ base: 6, md: 8 }}>
          <SectionHeader eyebrow="00 / About" title="Background" />

          <Flex
            direction={{ base: "column", md: "row" }}
            align={{ base: "center", md: "flex-start" }}
            gap={{ base: 8, md: 14 }}
          >
            <Image
              src="/Images/divij.jpg"
              alt="Photo of Divij Garg"
              borderRadius="sm"
              border="1px solid"
              borderColor="whiteAlpha.200"
              objectFit="cover"
              boxSize={{ base: "200px", md: "240px" }}
              flexShrink={0}
              filter="grayscale(0.15)"
            />

            <MotionBox
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5 }}
            >
              <Text
                fontSize={{ base: "md", md: "lg" }}
                color="whiteAlpha.700"
                fontWeight="400"
                lineHeight="1.9"
              >
                I am currently a Launch Pad Fluids Software Graduate Engineer at{" "}
                <Link href="https://www.spacex.com" isExternal {...linkStyles}>
                  SpaceX
                </Link>
                , with a background in guidance, navigation and control work
                advancing state estimation and controls within spacecraft.
                During my undergraduate degree, I led the GNC team for{" "}
                <Link
                  href="https://www.illinoisspacesociety.org/spaceshot"
                  isExternal
                  {...linkStyles}
                >
                  Illinois Space Society&apos;s
                </Link>{" "}
                spaceshot rocket. Previously, I interned twice at{" "}
                <Link href="https://varda.com" isExternal {...linkStyles}>
                  Varda Space Industries
                </Link>
                , handling satellite program management and coordinating teams
                and schedules across the vehicle lifecycle. Outside of the space
                industry, I love watching basketball, trying new foods, and
                watching The Office with my girlfriend.
              </Text>
            </MotionBox>
          </Flex>
        </Container>
      </Box>

      <FlightHeritage />
      <CompanyExperience />
      <Involvement />

      <Box id="simulation">
        <Satellite />
      </Box>

      {/* Footer */}
      <Box py={10} borderTop="1px solid" borderColor="whiteAlpha.200">
        <Container maxW="5xl" px={{ base: 6, md: 8 }}>
          <Flex
            direction={{ base: "column", md: "row" }}
            justify="space-between"
            align="center"
            gap={3}
          >
            <Text
              fontFamily="mono"
              fontSize="xs"
              letterSpacing="0.08em"
              color="whiteAlpha.500"
            >
              &copy; {new Date().getFullYear()} Divij Garg
            </Text>
            <Text
              fontFamily="mono"
              fontSize="xs"
              letterSpacing="0.08em"
              color="whiteAlpha.400"
            >
              Built with React &amp; Chakra UI
            </Text>
          </Flex>
        </Container>
      </Box>
    </Box>
  );
}

export default Home4;
